import { useEffect, useRef, useState } from 'react';

interface Props {
  onTranscript: (text: string) => void;
  lang?: string;
}

/**
 * Inline microphone button that streams a single utterance via Web Speech API.
 * Hidden gracefully if the browser doesn't support it (mostly Firefox).
 *
 * --- v2 rewrite (15 May 2026) ---
 *
 * The first version had robust internal safety nets (cross-component mic
 * coordination, unmount cleanup, iOS-onend safety timer, double-tap race
 * protection) but was SILENT on failure modes. Will reported "first tap
 * froze the app, second tap added no words" — both because the user got
 * no feedback distinguishing:
 *   - "tap registered, browser is waiting for mic permission"
 *   - "listening, but nothing said yet"
 *   - "ended, no speech detected"
 *   - "ended, mic permission denied"
 *   - "ended, network failure (Chrome cloud STT)"
 *
 * The fix: a phase state machine + a small caption next to the button
 * that surfaces what's actually happening. Interim results visible so
 * the user knows the mic IS working before they finish speaking.
 *
 * --- Reliability guards (carried over from v1) ---
 *
 * 1. **Cross-component coordination.** Browsers only allow one
 *    SpeechRecognition to be active per page. Module-level
 *    `activeRecognition` is force-aborted before any new mic starts.
 *
 * 2. **No cleanup on unmount.** useEffect cleanup calls abort().
 *
 * 3. **iOS Safari sometimes drops `onend`.** 60-second safety timer
 *    force-resets state.
 *
 * 4. **Race condition on rapid double-tap.** `phase === 'starting'`
 *    blocks re-entry; abort() (not stop()) for forced shutdowns
 *    because stop() is sometimes ignored on iOS.
 */

type Phase =
  | 'idle'        // not listening, no recent result
  | 'starting'    // tap registered, awaiting browser permission / onstart
  | 'listening'   // mic open, capturing audio
  | 'no-speech'   // ended without producing any transcript
  | 'error';      // some error fired (permission denied, network, etc.)

interface StatusMsg {
  text: string;
  tone: 'neutral' | 'error' | 'warn';
}

// Module-level reference to whichever DictateButton is currently recording.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let activeRecognition: any = null;

const MAX_RECORDING_MS = 60_000;
const TRANSIENT_MSG_MS = 3_500;

export function DictateButton({ onTranscript, lang = 'en-GB' }: Props) {
  const [supported, setSupported] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [interim, setInterim] = useState('');
  const [transient, setTransient] = useState<StatusMsg | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recRef = useRef<any>(null);
  const safetyTimerRef = useRef<number | null>(null);
  const transientTimerRef = useRef<number | null>(null);
  // Track whether we ever got a final transcript this session — used to
  // distinguish "got something, all good" from "ended with nothing".
  const gotFinalRef = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      forceCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearSafetyTimer() {
    if (safetyTimerRef.current !== null) {
      window.clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  }

  function clearTransientTimer() {
    if (transientTimerRef.current !== null) {
      window.clearTimeout(transientTimerRef.current);
      transientTimerRef.current = null;
    }
  }

  function showTransient(msg: StatusMsg) {
    clearTransientTimer();
    setTransient(msg);
    transientTimerRef.current = window.setTimeout(() => {
      setTransient(null);
      transientTimerRef.current = null;
    }, TRANSIENT_MSG_MS);
  }

  function forceCleanup() {
    clearSafetyTimer();
    if (recRef.current) {
      try { recRef.current.abort(); } catch { /* noop */ }
      if (activeRecognition === recRef.current) activeRecognition = null;
      recRef.current = null;
    }
    setPhase('idle');
    setInterim('');
  }

  /** Map browser error code → user-readable message. */
  function describeError(code: string): string {
    switch (code) {
      case 'not-allowed':
      case 'service-not-allowed':
        return 'Microphone permission denied. Check your browser settings.';
      case 'no-speech':
        return "Didn't catch anything — tap to try again.";
      case 'audio-capture':
        return 'No microphone available on this device.';
      case 'network':
        return 'Network issue — speech recognition needs a connection.';
      case 'aborted':
        // We aborted it ourselves; not a real error from the user's POV.
        return '';
      case 'language-not-supported':
        return `Language ${lang} not supported by this browser.`;
      default:
        return `Recognition error (${code || 'unknown'}).`;
    }
  }

  function start() {
    // Block re-entry while we're mid-start
    if (phase === 'starting' || phase === 'listening') return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;

    // Force-abort any other mic on the page first.
    if (activeRecognition && activeRecognition !== recRef.current) {
      try { activeRecognition.abort(); } catch { /* noop */ }
      activeRecognition = null;
    }

    setPhase('starting');
    setInterim('');
    gotFinalRef.current = false;
    setTransient(null);
    clearTransientTimer();

    const r = new SR();
    r.continuous = false;
    // interim results visible — gives user feedback that the mic IS hearing them
    r.interimResults = true;
    r.lang = lang;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onresult = (e: any) => {
      // Walk results to find final + latest interim
      let finalText = '';
      let interimText = '';
      for (let i = e.resultIndex ?? 0; i < e.results.length; i++) {
        const res = e.results[i];
        const t = res[0]?.transcript ?? '';
        if (res.isFinal) finalText += t;
        else interimText += t;
      }
      if (interimText) setInterim(interimText.trim());
      if (finalText) {
        gotFinalRef.current = true;
        setInterim('');
        onTranscript(finalText.trim());
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onerror = (e: any) => {
      const code = e?.error ?? 'unknown';
      const msg = describeError(code);
      forceCleanup();
      if (msg) {
        // 'no-speech' / 'aborted' return empty msg via describeError — don't
        // shout at the user for not having said anything. Other errors do.
        setPhase('error');
        showTransient({ text: msg, tone: 'error' });
        // Bounce back to idle after the toast clears so the button is reusable
        window.setTimeout(() => {
          setPhase((p) => (p === 'error' ? 'idle' : p));
        }, TRANSIENT_MSG_MS);
      }
    };

    r.onend = () => {
      const hadFinal = gotFinalRef.current;
      forceCleanup();
      if (!hadFinal) {
        // Ended cleanly but produced nothing — most often iOS silence
        // detection misfiring, or user tapped to stop before speaking,
        // or the well-known iOS Safari "drops onresult" bug.
        setPhase('no-speech');
        showTransient({ text: "Didn't catch that — tap to try again.", tone: 'warn' });
        window.setTimeout(() => {
          setPhase((p) => (p === 'no-speech' ? 'idle' : p));
        }, TRANSIENT_MSG_MS);
      }
    };

    r.onstart = () => {
      setPhase('listening');
      clearSafetyTimer();
      safetyTimerRef.current = window.setTimeout(() => {
        // iOS Safari sometimes drops onend after 60s; force-reset.
        forceCleanup();
      }, MAX_RECORDING_MS);
    };

    try {
      r.start();
      recRef.current = r;
      activeRecognition = r;
    } catch (err) {
      // start() throws if another recognition is somehow still running, or
      // if mic permission was just revoked. Reset clean and surface.
      forceCleanup();
      setPhase('error');
      showTransient({
        text: 'Couldn’t start microphone. Try again, or check permissions.',
        tone: 'error',
      });
      window.setTimeout(() => {
        setPhase((p) => (p === 'error' ? 'idle' : p));
      }, TRANSIENT_MSG_MS);
    }
  }

  function stop() {
    // abort() = force-quit; iOS sometimes ignores stop()
    if (recRef.current) {
      try { recRef.current.abort(); } catch { /* noop */ }
    }
    forceCleanup();
  }

  if (!supported) return null;

  const isActive = phase === 'starting' || phase === 'listening';
  const statusText =
    transient?.text
    ?? (phase === 'starting' ? 'Starting…'
    : (phase === 'listening' && interim) ? `“${interim}”`
    : phase === 'listening' ? 'Listening…'
    : '');
  const statusTone: 'neutral' | 'error' | 'warn' =
    transient?.tone
    ?? (phase === 'error' ? 'error'
    : phase === 'no-speech' ? 'warn'
    : 'neutral');

  return (
    <span className="dictate-shell">
      <button
        type="button"
        className={`dictate-btn ${isActive ? 'recording' : ''} ${phase === 'starting' ? 'starting' : ''}`}
        onClick={isActive ? stop : start}
        aria-label={isActive ? 'Stop dictation' : 'Tap to dictate'}
        aria-pressed={isActive}
        title={
          isActive ? 'Tap to stop'
          : transient?.text ? transient.text
          : 'Tap to dictate'
        }
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
        </svg>
        {isActive && <span className="dictate-pulse" aria-hidden="true" />}
      </button>
      {statusText && (
        <span
          className={`dictate-status dictate-status-${statusTone}`}
          role="status"
          aria-live="polite"
        >
          {statusText}
        </span>
      )}
    </span>
  );
}
